import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { toast } from 'sonner';
import {
  Home, FolderOpen, Store, Gift, User as UserIcon, CreditCard, Shield, Globe, Paintbrush, Key,
  Crown, Settings2, LogOut, ChevronsUpDown, PanelLeft, Menu, X, Zap, Building2, LayoutTemplate,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useIsMobile } from '@/hooks/use-mobile';
import ThemeToggle from '@/components/ThemeToggle';
import NotificationBell from '@/components/NotificationBell';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import MySitesSection from '@/components/generation/MySitesSection';
import { StartFromAnIdea } from '@/components/home/ShowcaseGallery';
import PromptComposer from '@/components/generation/PromptComposer';
import { Tile, OrangeTile, Eyebrow } from '@/components/bento';
import MarketplaceShowcase from '@/components/marketplace/MarketplaceShowcase';
import SellSiteDialog from '@/components/marketplace/SellSiteDialog';
import EarnSection from '@/components/dashboard/EarnSection';
import MarketplaceSection from '@/components/dashboard/MarketplaceSection';
import CompanyComposer from '@/components/business/CompanyComposer';
import MyCompanies from '@/components/business/MyCompanies';
import { messageCreditsForTier, PLANS } from '@/config/plans';
import { canBuyCreditPacks } from '@/config/pricing';
import { REFERRAL_DISCOUNT } from '@/config/earnings';
// The account panels are the same components the standalone Settings page
// renders. Imported rather than reimplemented so the two can never drift, and
// so a fix to billing or security lands in both places at once.
import {
  ProfileTab, BillingTab, SecurityTab, CustomDomainsTab, BrandingTab, ApiKeysTab,
} from '@/pages/Settings';

// ─── The account dashboard ────────────────────────────────────────────────────
// What a signed-in person lands on: their sites, the marketplace, what they
// earn, and their account. It is NOT a site workspace, so it has no Preview,
// Dashboard or Code tabs — those belong to a site that is open or being built,
// and showing them here (with nothing to preview and no code) was the thing
// people found confusing. A site's own dashboard (overview, versions, settings,
// activity) is the Dashboard tab inside that site's workspace.
//
// The section lives in the URL (?section=…), so "View all", the Settings page's
// links and the browser's Back button all land where they say.

const PLAN_NAMES = { free: 'Free', starter: 'Starter', pro: 'Pro', agency: 'Studio' };

export const SECTIONS = [
  { key: 'home',        label: 'Home',             icon: Home,       group: null },
  { key: 'sites',       label: 'My sites',         icon: FolderOpen, group: 'Build' },
  { key: 'companies',   label: 'My companies',     icon: Building2,  group: 'Build' },
  { key: 'marketplace', label: 'Marketplace',      icon: Store,      group: 'Build' },
  { key: 'earn',        label: 'Earn',             icon: Gift,       group: 'Earn' },
  { key: 'account',     label: 'Profile',          icon: UserIcon,   group: 'Account' },
  { key: 'billing',     label: 'Billing & credits', icon: CreditCard, group: 'Account' },
  { key: 'security',    label: 'Security',         icon: Shield,     group: 'Account' },
  { key: 'domains',     label: 'Custom domains',   icon: Globe,      group: 'Account' },
  { key: 'branding',    label: 'Branding',         icon: Paintbrush, group: 'Account' },
  { key: 'api',         label: 'API keys',         icon: Key,        group: 'Account' },
];
const SECTION_KEYS = new Set(SECTIONS.map(s => s.key));

// What a gateway's redirect back to the dashboard means.
const PAYMENT_MESSAGES = {
  flw: 'Payment successful — your plan is active.',
  crypto: 'Payment received — your plan is active.',
  credits: 'Payment received — your extra credits are in your balance.',
  marketplace: 'Payment received — your new site is in My sites.',
};
const PAYMENT_ERRORS = {
  amount_mismatch: 'The payment did not match the price, so nothing was added. If you were charged, contact support.',
  payment_not_verified: 'That payment did not go through. Nothing was charged.',
  missing_meta: 'We could not match that payment. If you were charged, contact support.',
  server_error: 'Something went wrong confirming that payment. If you were charged, it will appear shortly — or contact support.',
  site_not_found: 'We could not find that site. If you were charged, contact support.',
};

function AccountPanel({ title, icon: Icon, children }) {
  return (
    <div className="p-4 sm:p-6 max-w-3xl">
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5 sm:p-6">
        <h2 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
          <Icon className="w-5 h-5 text-orange-400" />{title}
        </h2>
        {children}
      </div>
    </div>
  );
}

function MenuItem({ icon: Icon, children, onClick, accent = false, danger = false }) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs transition-colors ${
        danger ? 'text-red-400 hover:bg-red-500/10'
          : accent ? 'text-orange-300 hover:bg-orange-500/10'
            : 'text-zinc-300 hover:bg-zinc-900 hover:text-white'
      }`}
    >
      <Icon className="w-3.5 h-3.5 shrink-0" />
      <span className="flex-1 text-left">{children}</span>
    </button>
  );
}

/**
 * The pinned bottom of the sidebar: the upgrade prompt, then the account
 * button with Settings, Billing and Sign out.
 */
function SidebarFooter({ profile, user, onSection, onSignOut, collapsed = false, navigate }) {
  const [open, setOpen] = useState(false);
  const tier = profile?.subscription_tier || 'free';
  const canUpgrade = tier !== 'agency';
  const name = (profile?.full_name || user?.user_metadata?.full_name || '').trim();
  const email = profile?.email || user?.email || '';
  const avatar = profile?.avatar_url || user?.user_metadata?.avatar_url;
  const initials = (name || email || 'U').trim().slice(0, 1).toUpperCase();
  const credits = profile?.credits;
  const limit = profile?.plan_limit ?? messageCreditsForTier(tier);

  return (
    <div className="shrink-0 border-t border-white/5 p-2 flex flex-col gap-2">
      {canUpgrade && collapsed && (
        <button onClick={() => navigate('/Pricing')} title="Upgrade your plan" aria-label="Upgrade your plan"
          className="w-9 h-9 mx-auto rounded-xl bg-zinc-950 grid place-items-center ring-1 ring-orange-500/25 hover:ring-orange-500/50 transition-colors">
          <Crown className="w-4 h-4 text-orange-400" />
        </button>
      )}
      {canUpgrade && !collapsed && (
        <button onClick={() => navigate('/Pricing')}
          className="w-full text-left rounded-2xl border border-orange-500/25 bg-orange-500/[0.07] hover:bg-orange-500/[0.12] transition-colors px-3 py-2.5">
          <span className="flex items-center gap-2 text-xs font-semibold text-orange-300">
            <span className="w-6 h-6 rounded-md bg-zinc-950 grid place-items-center shrink-0 ring-1 ring-orange-500/25">
              <Crown className="w-3.5 h-3.5 text-orange-400" />
            </span>
            Upgrade your plan
          </span>
          <span className="block text-[11px] text-zinc-500 mt-1 leading-snug pl-8">
            {typeof credits === 'number' ? `${credits} of ${limit} credits left` : 'More credits, no badge'}
          </span>
        </button>
      )}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            className={`w-full flex items-center gap-2.5 py-2 rounded-xl hover:bg-zinc-900 transition-colors text-left ${collapsed ? 'justify-center px-0' : 'px-2'}`}
            aria-label="Account menu"
            title={collapsed ? (name || 'Your account') : undefined}
          >
            {avatar
              ? <img src={avatar} alt="" className="w-7 h-7 rounded-lg object-cover shrink-0" />
              : <span className="w-7 h-7 rounded-lg bg-orange-500 text-ink text-xs font-bold flex items-center justify-center shrink-0">{initials}</span>}
            {!collapsed && (
              <>
                <span className="min-w-0 flex-1">
                  <span className="block text-xs font-medium text-zinc-200 truncate">{name || 'Your account'}</span>
                  <span className="block text-[10px] text-zinc-500 truncate">{PLAN_NAMES[tier] || 'Free'} plan</span>
                </span>
                <ChevronsUpDown className="w-3.5 h-3.5 text-zinc-600 shrink-0" />
              </>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent side="top" align="start" sideOffset={8} className="w-60 bg-zinc-950 border-zinc-800 text-zinc-200 p-1.5">
          <div className="px-2.5 py-2 border-b border-zinc-800 mb-1">
            <p className="text-xs font-medium text-white truncate">{name || 'Your account'}</p>
            {email && <p className="text-[11px] text-zinc-500 truncate">{email}</p>}
          </div>
          {canUpgrade && (
            <MenuItem icon={Crown} onClick={() => { setOpen(false); navigate('/Pricing'); }} accent>Upgrade your plan</MenuItem>
          )}
          <MenuItem icon={Settings2} onClick={() => { setOpen(false); onSection('account'); }}>Profile & settings</MenuItem>
          <MenuItem icon={CreditCard} onClick={() => { setOpen(false); onSection('billing'); }}>Billing & credits</MenuItem>
          <MenuItem icon={Gift} onClick={() => { setOpen(false); onSection('earn'); }}>Referrals & earnings</MenuItem>
          {/* Owners and team members (Admin → Team) only: the server puts
              their role on the profile, and checks it again in the console. */}
          {profile?.staff_role && (
            <MenuItem icon={Shield} onClick={() => { setOpen(false); navigate('/Admin'); }} accent>Team console</MenuItem>
          )}
          <div className="border-t border-zinc-800 mt-1 pt-1">
            <MenuItem icon={LogOut} onClick={() => { setOpen(false); onSignOut(); }} danger>Sign out</MenuItem>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

/**
 * Website or Company: one prompt box, two things it can make (Business plan
 * §2.1). Not a separate button, because people already know how the box works.
 *
 * Company is tagged "New": BuildersLab Business launched on 2026-10-20, and
 * people who have only ever built websites stop reading a switch they have
 * seen before. Take the tag off (`fresh`) once it is not news.
 *
 * On a phone it is two equal halves across the card; beside the greeting from
 * sm up. The arrow keys move between the two, as in any tab list.
 */
function BuildSwitch({ value, onChange }) {
  const options = [
    { key: 'website', label: 'Website', icon: LayoutTemplate },
    { key: 'company', label: 'Company', icon: Building2, fresh: true },
  ];
  const refs = useRef({});
  const onKeyDown = (e) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const next = options[(options.findIndex(o => o.key === value) + 1) % options.length].key;
    onChange(next);
    refs.current[next]?.focus();
  };
  return (
    <div role="tablist" aria-label="What to build" onKeyDown={onKeyDown}
      className="grid grid-cols-2 w-full rounded-full bg-zinc-950 border border-zinc-800 p-1 sm:inline-flex sm:w-auto sm:self-auto">
      {options.map(o => (
        <button key={o.key} ref={(el) => { refs.current[o.key] = el; }} role="tab" type="button"
          aria-selected={value === o.key} tabIndex={value === o.key ? 0 : -1} onClick={() => onChange(o.key)}
          className={`inline-flex items-center justify-center gap-1.5 rounded-full h-10 sm:h-8 px-3.5 text-sm sm:text-xs font-bold whitespace-nowrap transition-colors outline-none focus-visible:ring-2 focus-visible:ring-orange-500 ${
            value === o.key ? 'bg-zinc-800 text-white shadow-sm' : 'text-zinc-400 hover:text-white'
          }`}>
          <o.icon className="w-4 h-4 sm:w-3.5 sm:h-3.5" aria-hidden="true" />{o.label}
          {o.fresh && (
            <span className="ml-0.5 rounded-full bg-orange-500 px-1.5 py-[3px] text-[9px] font-extrabold uppercase leading-none tracking-wider text-ink">
              New
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

/**
 * The landing view for a returning user: the same "What do you wanna build?"
 * the home page asks, their credits and plan, ideas, the marketplace, and
 * their sites.
 */
function HomeSection({ profile, user, navigate, onSection, onSell, making, setMaking }) {
  const isPaid = ['starter', 'pro', 'agency'].includes(profile?.subscription_tier);
  const [prompt, setPrompt] = useState('');
  const reduceMotion = useReducedMotion();
  const composerRef = useRef(null);

  const firstName = (profile?.full_name || user?.user_metadata?.full_name || '').trim().split(' ')[0];
  const tier = profile?.subscription_tier || 'free';
  const model = PLANS[tier]?.model;
  const credits = profile?.credits;
  const limit = profile?.plan_limit ?? messageCreditsForTier(profile?.subscription_tier);
  const loadingCredits = credits === null || credits === undefined;
  const pct = loadingCredits || !limit ? 0 : Math.max(0, Math.min(100, Math.round((credits / limit) * 100)));

  const launch = ({ prompt: text, image, planFirst }) => {
    if (!text) return;
    // Same entry the home page uses — the prompt travels in router state and
    // /build starts the plan step, or skips it when "Plan mode" is off.
    navigate('/build', { state: { prompt: text, image, planFirst } });
  };

  const loadIdea = (idea) => {
    setPrompt(idea);
    composerRef.current?.scrollIntoView?.({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
    toast.success('Prompt loaded — hit Build when ready!');
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-12">
        <Tile className="md:col-span-2 lg:col-span-8 lg:row-span-2 p-5 sm:p-8 flex flex-col min-w-0">
          {/* A phone stacks the greeting over the switch, which then spans the
              card; it used to wrap wherever it ran out of room, and drop to a
              second line on its own at 360px. */}
          <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
            <Eyebrow>{firstName ? `Hi, ${firstName}!` : 'Hi'}</Eyebrow>
            <BuildSwitch value={making} onChange={setMaking} />
          </div>
          <h2 className="mt-5 text-[2.125rem] sm:text-4xl md:text-5xl font-extrabold tracking-[-0.045em] leading-[1.06] text-white text-balance">
            {making === 'company' ? 'What company do you want to start?' : 'What do you wanna build?'}
          </h2>
          <p className="mt-3 text-zinc-400 text-[15px] sm:text-base max-w-xl leading-relaxed">
            {making === 'company'
              ? 'One sentence. We build the brand, website, ads and documents, then register it with you.'
              : 'Describe what you want to build in plain English.'}
          </p>
          {/* mt-auto sits the box on the card's bottom edge beside the credit
              tiles; the padding keeps it off the line above when the card is
              no taller than its contents. */}
          <div ref={composerRef} className="mt-6 sm:mt-8 lg:mt-auto lg:pt-6">
            {making === 'company'
              ? <CompanyComposer onCreated={(company) => navigate(`/company/${company.id}`)} />
              : <PromptComposer value={prompt} onChange={setPrompt} onSubmit={launch} submitLabel="Build" reduceMotion={reduceMotion} showPlanToggle />}
          </div>
        </Tile>

        <Tile className="lg:col-span-4 p-6 flex flex-col justify-between gap-6 min-h-[180px]">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[15px] font-bold text-white">Credits</span>
            {canBuyCreditPacks(tier) && (
              <button onClick={() => onSection('billing')} className="text-xs font-semibold text-orange-400 hover:text-orange-300 inline-flex items-center gap-1">
                <Zap className="w-3.5 h-3.5" /> Buy more
              </button>
            )}
          </div>
          <div>
            <div className="flex items-baseline gap-2">
              <span className="text-5xl font-extrabold tracking-[-0.05em] text-white tabular-nums leading-none">{loadingCredits ? '…' : credits}</span>
              <span className="text-sm font-semibold text-zinc-400">{loadingCredits ? 'Checking credits…' : `of ${limit} left`}</span>
            </div>
            <div className="mt-4 h-2 rounded-full bg-zinc-800 overflow-hidden" role="progressbar" aria-label="Credits left"
              aria-valuemin={0} aria-valuemax={limit} aria-valuenow={loadingCredits ? undefined : credits}>
              <div className="h-full rounded-full bg-orange-500 transition-[width] duration-500" style={{ width: `${pct}%` }} />
            </div>
          </div>
        </Tile>

        <OrangeTile as="button" type="button" onClick={() => navigate('/Pricing')}
          className="lg:col-span-4 p-6 text-left flex flex-col justify-between gap-6 min-h-[180px] transition-[filter] hover:brightness-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950">
          <span className="text-[15px] font-bold">Your plan</span>
          <span className="block">
            <span className="block text-5xl font-extrabold tracking-[-0.05em] leading-none">{PLAN_NAMES[tier] || 'Free'}</span>
            <span className="mt-3 flex items-center justify-between gap-3 text-sm font-semibold">
              <span className="truncate">{model ? `Built with ${model}` : `${limit} credits a month`}</span>
              <span className="shrink-0 underline decoration-2 underline-offset-4">See plans →</span>
            </span>
          </span>
        </OrangeTile>

        <StartFromAnIdea className="md:col-span-2 lg:col-span-12" onUseIdea={loadIdea} />

        <Tile className="md:col-span-2 lg:col-span-12 p-5 sm:p-8">
          <MySitesSection navigate={navigate} isPaid={isPaid} limit={6} onSell={onSell} onViewAll={() => onSection('sites')} />
        </Tile>

        <MyCompaniesTile navigate={navigate} />

        <MarketplaceShowcase className="md:col-span-2 lg:col-span-12" inviteWhenEmpty onSell={() => onSell(null)} />

        {/* Earn, said once on the landing view so people know it exists. */}
        <Tile as="button" type="button" onClick={() => onSection('earn')}
          className="md:col-span-2 lg:col-span-12 p-5 sm:p-6 text-left flex flex-col sm:flex-row sm:items-center gap-4 hover:border-zinc-700 transition-colors">
          <span className="w-11 h-11 rounded-2xl bg-orange-500/15 grid place-items-center shrink-0"><Gift className="w-5 h-5 text-orange-400" /></span>
          <span className="flex-1 min-w-0">
            <span className="block text-white font-bold">Share your referral code</span>
            <span className="block text-sm text-zinc-400 mt-0.5">
              Friends get {REFERRAL_DISCOUNT} off any plan, and you earn a commission on what they pay.
              {profile?.referral_code ? <> Your code: <span className="font-mono font-semibold text-white">{profile.referral_code}</span></> : null}
            </span>
          </span>
          <span className="text-sm font-semibold text-orange-400 shrink-0">Open Earn →</span>
        </Tile>
      </div>
    </div>
  );
}

/** My companies on the landing view, once there is one to show. */
function MyCompaniesTile({ navigate }) {
  const [shown, setShown] = useState(true);
  if (!shown) return null;
  return (
    <Tile className="md:col-span-2 lg:col-span-12 p-5 sm:p-8">
      <MyCompanies navigate={navigate} limit={3} hideWhenEmpty onEmpty={() => setShown(false)} />
    </Tile>
  );
}

export default function AccountDashboard() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const isMobile = useIsMobile();
  const { user, session, profile, setProfile, refreshProfile, signOut } = useAuth();

  const fromUrl = new URLSearchParams(search || '').get('section');
  const [section, setSectionState] = useState(SECTION_KEYS.has(fromUrl) ? fromUrl : 'home');
  const [collapsed, setCollapsed] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [selling, setSelling] = useState(null);   // null = closed; { site } when open
  // Website or Company, in the home composer. Held here so "Start a company"
  // from My companies can open the composer already switched.
  const [making, setMaking] = useState(() => (new URLSearchParams(search || '').get('mode') === 'company' ? 'company' : 'website'));

  // Follow the URL, so "View all" and links from Settings land where they say.
  useEffect(() => {
    if (fromUrl && SECTION_KEYS.has(fromUrl) && fromUrl !== section) setSectionState(fromUrl);
  }, [fromUrl]);

  const setSection = useCallback((key) => {
    setSectionState(key);
    setNavOpen(false);
    const params = new URLSearchParams(window.location.search);
    params.set('view', 'dashboard');
    if (key === 'home') params.delete('section'); else params.set('section', key);
    navigate(`/build?${params.toString()}`, { replace: true });
  }, [navigate]);

  // Credits change with every build, so the dashboard re-reads them on arrival.
  useEffect(() => { refreshProfile?.().catch?.(() => {}); }, [refreshProfile]);

  // The document behind the dashboard does not scroll; the panes do. Same
  // arrangement as the workspace (see .bl-app-shell in index.css).
  useEffect(() => {
    const html = document.documentElement;
    html.classList.add('bl-app-shell');
    return () => html.classList.remove('bl-app-shell');
  }, []);

  // A gateway sends the customer back here with ?success=… or ?error=…; this
  // is the only feedback they get that the money bought something. Said once:
  // the flags are taken out of the address straight after.
  useEffect(() => {
    const params = new URLSearchParams(search || '');
    const ok = params.get('success');
    const err = params.get('error');
    const pending = params.get('pending');
    if (!ok && !err && !pending) return;
    if (ok) toast.success(PAYMENT_MESSAGES[ok] || 'Payment received.', { duration: 6000 });
    else if (pending) toast.info('Your transfer is being confirmed — it will be added as soon as it lands.', { duration: 6000 });
    else toast.error(PAYMENT_ERRORS[err] || 'That payment could not be completed.', { duration: 8000 });
    if (ok) refreshProfile?.().catch?.(() => {});
    ['success', 'error', 'pending', 'credits', 'refresh'].forEach(k => params.delete(k));
    params.set('view', 'dashboard');
    navigate(`/build?${params.toString()}`, { replace: true });
  }, [search, refreshProfile, navigate]);

  useEffect(() => {
    if (!isMobile || !navOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setNavOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isMobile, navOpen]);

  const tier = profile?.subscription_tier || 'free';
  const credits = profile?.credits;
  const isPaid = ['starter', 'pro', 'agency'].includes(tier);
  const railCollapsed = !isMobile && collapsed;

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };
  const openSell = (site) => setSelling({ site: site || null });
  const onProfileUpdate = (next) => setProfile?.(next);

  const groups = [null, 'Build', 'Earn', 'Account'];

  // A render function rather than a component declared in here, which would be
  // a new component type on every render and remount every button.
  const navButton = (item) => (
    <button
      key={item.key}
      onClick={() => setSection(item.key)}
      title={railCollapsed ? item.label : undefined}
      aria-label={item.label}
      aria-current={section === item.key ? 'page' : undefined}
      className={`w-full flex items-center gap-2.5 py-2 text-sm transition-colors rounded-lg ${railCollapsed ? 'justify-center px-0' : 'px-3'} ${
        section === item.key ? 'bg-zinc-800 text-white font-medium' : 'text-zinc-400 hover:text-white hover:bg-zinc-900/60'
      }`}
    >
      <item.icon className={`w-4 h-4 shrink-0 ${section === item.key ? 'text-orange-400' : ''}`} />
      {railCollapsed ? null : <span className="flex-1 text-left truncate">{item.label}</span>}
    </button>
  );

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="fixed inset-0 bg-zinc-950 flex flex-col overflow-hidden overscroll-none">
      {/* ══ TOP BAR ══ — a console header: no site title and no Preview /
          Dashboard / Code tabs, because no site is open here.
          `relative z-30`: backdrop-blur makes the header its own stacking
          context, and the sections below (animated, so also their own) were
          painted over it — the notifications panel opened underneath "What do
          you wanna build". Above the page, below the phone menu (z-40/50). */}
      <header className="relative z-30 h-14 shrink-0 border-b border-zinc-800 bg-zinc-950/85 backdrop-blur flex items-center gap-2 px-3 md:px-4">
        {isMobile ? (
          <button onClick={() => setNavOpen(v => !v)} aria-label={navOpen ? 'Close menu' : 'Open menu'} aria-expanded={navOpen}
            className="h-9 w-9 grid place-items-center rounded-lg text-zinc-300 active:text-white active:bg-white/5">
            <Menu className="w-5 h-5" />
          </button>
        ) : (
          <button onClick={() => setCollapsed(v => !v)} aria-label={collapsed ? 'Open sidebar' : 'Close sidebar'} aria-pressed={collapsed}
            title={collapsed ? 'Open sidebar' : 'Close sidebar'}
            className="h-8 w-8 grid place-items-center rounded-lg text-zinc-400 hover:text-white hover:bg-white/5 transition-colors">
            <PanelLeft className="w-4 h-4" />
          </button>
        )}
        <button onClick={() => setSection('home')} aria-label="Dashboard home" className="flex items-center gap-2 rounded-lg px-1 py-1 hover:bg-white/5 transition-colors">
          <img src="/images/builderslab-logo.png" alt="" className="h-6 w-auto" />
          <span className="text-white font-bold text-sm tracking-tight hidden sm:inline">BuildersLab</span>
        </button>

        <div className="ml-auto flex items-center gap-1.5 md:gap-2">
          <button onClick={() => setSection('billing')}
            title={typeof credits === 'number' ? `${credits} credits left` : undefined}
            className="flex items-center bg-zinc-900 border border-zinc-800 rounded-full px-2.5 md:px-3 py-1 hover:border-zinc-700 transition-colors">
            <span className="text-[11px] font-semibold tabular-nums whitespace-nowrap text-orange-400">
              {typeof credits === 'number' ? `${credits} ${credits === 1 ? 'credit' : 'credits'} left` : '…'}
            </span>
          </button>
          {canBuyCreditPacks(tier) ? (
            <button onClick={() => setSection('billing')}
              className="hidden md:inline-flex h-7 items-center px-3 text-[11px] bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-300 rounded-full">
              <Zap className="w-3 h-3 mr-1 text-orange-400" /> Buy credits
            </button>
          ) : (
            <button onClick={() => navigate('/Pricing')}
              className="hidden md:inline-flex h-7 items-center px-3 text-[11px] bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-300 rounded-full">
              <Zap className="w-3 h-3 mr-1 text-orange-400" /> Upgrade
            </button>
          )}
          {/* On every screen size, beside the credits: the phone header had
              no way to switch between dark and light. */}
          <ThemeToggle size="sm" />
          {user && <NotificationBell user={user} />}
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {isMobile && navOpen && (
          <div className="fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px]" onClick={() => setNavOpen(false)} aria-hidden="true" />
        )}

        <nav
          aria-label="Dashboard"
          {...(isMobile ? { role: 'dialog', 'aria-modal': true } : {})}
          className={isMobile
            ? `fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] border-r border-white/10 bg-zinc-950 flex flex-col overflow-hidden shadow-2xl pb-[env(safe-area-inset-bottom)] transition-[transform,visibility] duration-200 ${
                navOpen ? 'translate-x-0 visible' : '-translate-x-full invisible'
              }`
            : `${railCollapsed ? 'w-14' : 'w-56'} shrink-0 border-r border-white/5 bg-zinc-950 flex flex-col overflow-hidden transition-[width] duration-200`}
        >
          {isMobile && (
            <div className="h-14 shrink-0 flex items-center justify-between pl-4 pr-2 border-b border-white/5">
              <span className="flex items-center gap-2">
                <img src="/images/builderslab-logo.png" alt="" className="h-6 w-auto" />
                <span className="text-white font-bold text-sm tracking-tight">BuildersLab</span>
              </span>
              <button onClick={() => setNavOpen(false)} aria-label="Close menu" className="h-10 w-10 grid place-items-center rounded-lg text-zinc-400 active:text-white active:bg-white/5">
                <X className="w-5 h-5" />
              </button>
            </div>
          )}
          <div className="flex-1 overflow-y-auto custom-scrollbar p-2 gap-0.5 flex flex-col">
            {groups.map(group => (
              <React.Fragment key={group || 'top'}>
                {group && (railCollapsed
                  ? <div className="mx-auto my-2 w-6 h-px bg-zinc-800" />
                  : <p className="px-3 pt-4 pb-1.5 text-[10px] text-zinc-600 uppercase tracking-widest font-semibold">{group}</p>)}
                {SECTIONS.filter(s => s.group === group).map(navButton)}
              </React.Fragment>
            ))}
          </div>
          <SidebarFooter
            profile={profile} user={user} collapsed={railCollapsed} navigate={navigate}
            onSection={setSection} onSignOut={handleSignOut}
          />
        </nav>

        <main className="flex-1 overflow-y-auto custom-scrollbar relative">
          <AnimatePresence mode="wait">
            <motion.div key={section} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.15 }}>
              {section === 'home' && (
                <HomeSection profile={profile} user={user} navigate={navigate} onSection={setSection} onSell={openSell} making={making} setMaking={setMaking} />
              )}
              {section === 'sites' && (
                <div className="p-4 sm:p-6 max-w-6xl">
                  <MySitesSection navigate={navigate} isPaid={isPaid} onSell={openSell} title="My sites" />
                </div>
              )}
              {section === 'companies' && (
                <div className="p-4 sm:p-6 max-w-6xl">
                  <MyCompanies navigate={navigate} onStart={() => { setMaking('company'); setSection('home'); }} />
                </div>
              )}
              {section === 'marketplace' && <MarketplaceSection navigate={navigate} />}
              {section === 'earn' && <EarnSection onOpenListings={() => setSection('marketplace')} />}
              {section === 'account' && (
                <AccountPanel title="Profile" icon={UserIcon}>
                  <ProfileTab user={user} profile={profile} onProfileUpdate={onProfileUpdate} onOpenEarn={() => setSection('earn')} />
                </AccountPanel>
              )}
              {section === 'billing' && <AccountPanel title="Billing & credits" icon={CreditCard}><BillingTab session={session} profile={profile} /></AccountPanel>}
              {section === 'security' && <AccountPanel title="Security" icon={Shield}><SecurityTab user={user} /></AccountPanel>}
              {section === 'domains' && <AccountPanel title="Custom domains" icon={Globe}><CustomDomainsTab profile={profile} /></AccountPanel>}
              {section === 'branding' && <AccountPanel title="Branding" icon={Paintbrush}><BrandingTab profile={profile} onProfileUpdate={onProfileUpdate} /></AccountPanel>}
              {section === 'api' && <AccountPanel title="API keys" icon={Key}><ApiKeysTab session={session} profile={profile} /></AccountPanel>}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>

      <SellSiteDialog open={!!selling} site={selling?.site || null} onClose={() => setSelling(null)} />

      <style dangerouslySetInnerHTML={{ __html: `
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #27272a; border-radius: 4px; }
      `}} />
    </motion.div>
  );
}
